import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * Generate forms for the cross-border payment rails (SWIFT / ACH / SEPA / Wise).
 * Each posts the exact body its controller route expects; amounts prefill from
 * the payment-run items. Bank-specific fields (BIC, routing, IBAN) are operator
 * input. Zengin has its own form in PaymentRunDrawer.
 */

export interface RailItem {
  id: string;
  ap_invoice_id: string | null;
  expense_report_id: string | null;
  amount_cents: string;
  currency: string;
}

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">
        {label}
        {hint && <span className="ml-1 normal-case tracking-normal text-ink-400">({hint})</span>}
      </label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function todayYYMMDD(): string {
  const d = new Date();
  return `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}
function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
function refOf(it: RailItem): string {
  return (it.ap_invoice_id ?? it.expense_report_id ?? it.id).slice(0, 16);
}

function useGenerate(wsId: string, runId: string, rail: string, onGenerated: () => void, onError: (m: string) => void) {
  return useMutation({
    mutationFn: async (body: unknown) => (await api.post(`/workspaces/${wsId}/payment-runs/${runId}/${rail}`, body)).data,
    onSuccess: () => onGenerated(),
    onError: (err: any) => onError(err?.response?.data?.message ?? err?.message ?? 'Generate failed'),
  });
}

const Section = ({ children }: { children: React.ReactNode }) => (
  <section className="rounded-lg border border-ink-200 dark:border-ink-800 p-3 bg-ink-50 dark:bg-ink-900/50 space-y-4">{children}</section>
);
const SubmitBtn = ({ onClick, disabled, label }: { onClick: () => void; disabled: boolean; label: string }) => (
  <div className="flex justify-end">
    <button type="button" onClick={onClick} disabled={disabled} className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50">
      {label}
    </button>
  </div>
);

interface FormProps {
  wsId: string;
  runId: string;
  items: RailItem[];
  baseCurrency: string;
  onGenerated: () => void;
  onError: (msg: string) => void;
}

// ── SWIFT MT103 ──────────────────────────────────────────────────────
function SwiftForm({ wsId, runId, items, onGenerated, onError }: FormProps) {
  const { t } = useTranslation();
  const [senderBic, setSenderBic] = useState('');
  const [orderAccount, setOrderAccount] = useState('');
  const [orderName, setOrderName] = useState('');
  const [rows, setRows] = useState(
    items.map((it) => ({
      reference: refOf(it),
      valueDateYYMMDD: todayYYMMDD(),
      currencyAmount: `${it.currency}${(Number(it.amount_cents) / 100).toFixed(2).replace('.', ',')}`,
      beneficiaryBic: '',
      beneficiaryAccount: '',
      beneficiaryName: '',
      remittance: '',
      charges: 'SHA' as 'SHA' | 'OUR' | 'BEN',
    })),
  );
  const gen = useGenerate(wsId, runId, 'swift', onGenerated, onError);
  const submit = () =>
    gen.mutate({
      sender: { bic: senderBic },
      beneficiaries: rows.map((r) => ({
        reference: r.reference,
        valueDateYYMMDD: r.valueDateYYMMDD,
        currencyAmount: r.currencyAmount,
        orderingCustomer: { account: orderAccount, name: orderName },
        beneficiaryBic: r.beneficiaryBic,
        beneficiary: { account: r.beneficiaryAccount, name: r.beneficiaryName },
        remittance: r.remittance,
        charges: r.charges,
      })),
    });
  const set = (i: number, patch: Partial<(typeof rows)[number]>) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <Section>
      <div className="grid grid-cols-3 gap-3">
        <Field label={t('rails.senderBic')} hint="8 or 11"><input value={senderBic} onChange={(e) => setSenderBic(e.target.value.toUpperCase())} className={inputCn} /></Field>
        <Field label={t('rails.orderingAccount')}><input value={orderAccount} onChange={(e) => setOrderAccount(e.target.value)} className={inputCn} /></Field>
        <Field label={t('rails.orderingName')}><input value={orderName} onChange={(e) => setOrderName(e.target.value)} className={inputCn} /></Field>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 p-2 rounded-lg border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900">
            <Field label={t('rails.beneficiaryName')}><input value={r.beneficiaryName} onChange={(e) => set(i, { beneficiaryName: e.target.value })} className={inputCn} /></Field>
            <Field label={t('rails.beneficiaryBic')}><input value={r.beneficiaryBic} onChange={(e) => set(i, { beneficiaryBic: e.target.value.toUpperCase() })} className={inputCn} /></Field>
            <Field label={t('rails.beneficiaryAccount')} hint="IBAN/acct"><input value={r.beneficiaryAccount} onChange={(e) => set(i, { beneficiaryAccount: e.target.value })} className={inputCn} /></Field>
            <Field label={t('rails.currencyAmount')} hint="32A"><input value={r.currencyAmount} onChange={(e) => set(i, { currencyAmount: e.target.value })} className={inputCn} /></Field>
            <Field label={t('rails.remittance')}><input value={r.remittance} onChange={(e) => set(i, { remittance: e.target.value })} className={inputCn} /></Field>
            <Field label={t('rails.charges')}>
              <select value={r.charges} onChange={(e) => set(i, { charges: e.target.value as 'SHA' | 'OUR' | 'BEN' })} className={inputCn}>
                <option value="SHA">SHA</option><option value="OUR">OUR</option><option value="BEN">BEN</option>
              </select>
            </Field>
          </div>
        ))}
      </div>
      <SubmitBtn onClick={submit} disabled={gen.isPending} label={t('rails.generate')} />
    </Section>
  );
}

// ── NACHA ACH ────────────────────────────────────────────────────────
function AchForm({ wsId, runId, items, onGenerated, onError }: FormProps) {
  const { t } = useTranslation();
  const [s, setS] = useState({
    immediateDestination: '', immediateOrigin: '', immediateDestinationName: '', immediateOriginName: '',
    companyName: '', companyId: '', originatingDfi: '', fileIdModifier: 'A',
  });
  const [effective, setEffective] = useState(todayYYMMDD());
  const [sec, setSec] = useState<'PPD' | 'CCD'>('CCD');
  const [rows, setRows] = useState(
    items.map((it) => ({ recvRouting: '', recvAccount: '', amountCents: Number(it.amount_cents), individualName: '', transactionCode: '22' as '22' | '32' })),
  );
  const gen = useGenerate(wsId, runId, 'ach', onGenerated, onError);
  const set = (i: number, patch: Partial<(typeof rows)[number]>) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const submit = () => gen.mutate({ sender: s, entries: rows, effectiveYYMMDD: effective, sec });
  const sf = (k: keyof typeof s) => (e: React.ChangeEvent<HTMLInputElement>) => setS({ ...s, [k]: e.target.value });
  return (
    <Section>
      <div className="grid grid-cols-3 gap-3">
        <Field label={t('rails.companyName')}><input value={s.companyName} onChange={sf('companyName')} className={inputCn} /></Field>
        <Field label={t('rails.companyId')}><input value={s.companyId} onChange={sf('companyId')} className={inputCn} /></Field>
        <Field label={t('rails.originatingDfi')} hint="8"><input value={s.originatingDfi} onChange={sf('originatingDfi')} className={inputCn} /></Field>
        <Field label={t('rails.immediateDestination')} hint="ABA"><input value={s.immediateDestination} onChange={sf('immediateDestination')} className={inputCn} /></Field>
        <Field label={t('rails.immediateOrigin')}><input value={s.immediateOrigin} onChange={sf('immediateOrigin')} className={inputCn} /></Field>
        <Field label={t('rails.effectiveDate')} hint="YYMMDD"><input value={effective} onChange={(e) => setEffective(e.target.value.replace(/\D/g, '').slice(0, 6))} className={inputCn} /></Field>
        <Field label={t('rails.destinationName')}><input value={s.immediateDestinationName} onChange={sf('immediateDestinationName')} className={inputCn} /></Field>
        <Field label={t('rails.originName')}><input value={s.immediateOriginName} onChange={sf('immediateOriginName')} className={inputCn} /></Field>
        <Field label={t('rails.sec')}>
          <select value={sec} onChange={(e) => setSec(e.target.value as 'PPD' | 'CCD')} className={inputCn}><option value="CCD">CCD</option><option value="PPD">PPD</option></select>
        </Field>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 p-2 rounded-lg border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900">
            <input value={r.individualName} onChange={(e) => set(i, { individualName: e.target.value })} placeholder={t('rails.recipient')} className={`${inputCn} col-span-4`} />
            <input value={r.recvRouting} onChange={(e) => set(i, { recvRouting: e.target.value.replace(/\D/g, '').slice(0, 9) })} placeholder={t('rails.routing')} className={`${inputCn} col-span-3`} />
            <input value={r.recvAccount} onChange={(e) => set(i, { recvAccount: e.target.value })} placeholder={t('rails.account')} className={`${inputCn} col-span-3`} />
            <input type="number" value={r.amountCents} onChange={(e) => set(i, { amountCents: Number(e.target.value) || 0 })} className={`${inputCn} col-span-2 text-right`} />
          </div>
        ))}
      </div>
      <SubmitBtn onClick={submit} disabled={gen.isPending} label={t('rails.generate')} />
    </Section>
  );
}

// ── SEPA pain.001 ────────────────────────────────────────────────────
function SepaForm({ wsId, runId, items, onGenerated, onError }: FormProps) {
  const { t } = useTranslation();
  const [s, setS] = useState({ msgId: `PLYEZ-${Date.now()}`, initiatorName: '', debtorName: '', debtorIban: '', debtorBic: '' });
  const [execDate, setExecDate] = useState(todayISO());
  const [rows, setRows] = useState(
    items.map((it) => ({ endToEndId: refOf(it), amountEur: Number(it.amount_cents) / 100, creditorName: '', creditorIban: '', creditorBic: '', remittance: '' })),
  );
  const gen = useGenerate(wsId, runId, 'sepa', onGenerated, onError);
  const set = (i: number, patch: Partial<(typeof rows)[number]>) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const sf = (k: keyof typeof s) => (e: React.ChangeEvent<HTMLInputElement>) => setS({ ...s, [k]: e.target.value });
  const submit = () => gen.mutate({ sender: s, payments: rows, requestedExecutionDate: execDate });
  return (
    <Section>
      <div className="grid grid-cols-3 gap-3">
        <Field label={t('rails.initiatorName')}><input value={s.initiatorName} onChange={sf('initiatorName')} className={inputCn} /></Field>
        <Field label={t('rails.debtorName')}><input value={s.debtorName} onChange={sf('debtorName')} className={inputCn} /></Field>
        <Field label={t('rails.executionDate')} hint="YYYY-MM-DD"><input type="date" value={execDate} onChange={(e) => setExecDate(e.target.value)} className={inputCn} /></Field>
        <Field label={t('rails.debtorIban')}><input value={s.debtorIban} onChange={sf('debtorIban')} className={inputCn} /></Field>
        <Field label={t('rails.debtorBic')} hint="opt"><input value={s.debtorBic} onChange={sf('debtorBic')} className={inputCn} /></Field>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 p-2 rounded-lg border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900">
            <Field label={t('rails.creditorName')}><input value={r.creditorName} onChange={(e) => set(i, { creditorName: e.target.value })} className={inputCn} /></Field>
            <Field label={t('rails.creditorIban')}><input value={r.creditorIban} onChange={(e) => set(i, { creditorIban: e.target.value })} className={inputCn} /></Field>
            <Field label={t('rails.creditorBic')} hint="opt"><input value={r.creditorBic} onChange={(e) => set(i, { creditorBic: e.target.value })} className={inputCn} /></Field>
            <Field label="EUR"><input type="number" value={r.amountEur} onChange={(e) => set(i, { amountEur: Number(e.target.value) || 0 })} className={`${inputCn} text-right`} /></Field>
            <div className="col-span-2"><Field label={t('rails.remittance')}><input value={r.remittance} onChange={(e) => set(i, { remittance: e.target.value })} className={inputCn} /></Field></div>
          </div>
        ))}
      </div>
      <SubmitBtn onClick={submit} disabled={gen.isPending} label={t('rails.generate')} />
    </Section>
  );
}

// ── Wise (single transfer) ───────────────────────────────────────────
function WiseForm({ wsId, runId, items, baseCurrency, onGenerated, onError }: FormProps) {
  const { t } = useTranslation();
  const first = items[0];
  const totalCents = items.reduce((s, it) => s + Number(it.amount_cents), 0);
  const [sourceCurrency, setSourceCurrency] = useState(baseCurrency);
  const [targetCurrency, setTargetCurrency] = useState(first?.currency ?? 'USD');
  const [targetAmount, setTargetAmount] = useState(totalCents / 100);
  const [holder, setHolder] = useState('');
  const [type, setType] = useState<'business' | 'personal'>('business');
  const [reference, setReference] = useState(first ? refOf(first) : '');
  const [detailsJson, setDetailsJson] = useState('{\n  "legalType": "BUSINESS"\n}');
  const gen = useGenerate(wsId, runId, 'wise', onGenerated, onError);
  const submit = () => {
    let details: Record<string, unknown>;
    try {
      details = JSON.parse(detailsJson || '{}');
    } catch {
      onError(t('rails.wiseDetailsInvalid'));
      return;
    }
    gen.mutate({ sourceCurrency, targetCurrency, targetAmount, recipient: { accountHolderName: holder, type, details }, reference });
  };
  return (
    <Section>
      <div className="grid grid-cols-3 gap-3">
        <Field label={t('rails.sourceCurrency')}><input value={sourceCurrency} onChange={(e) => setSourceCurrency(e.target.value.toUpperCase().slice(0, 3))} className={inputCn} /></Field>
        <Field label={t('rails.targetCurrency')}><input value={targetCurrency} onChange={(e) => setTargetCurrency(e.target.value.toUpperCase().slice(0, 3))} className={inputCn} /></Field>
        <Field label={t('rails.targetAmount')}><input type="number" value={targetAmount} onChange={(e) => setTargetAmount(Number(e.target.value) || 0)} className={`${inputCn} text-right`} /></Field>
        <Field label={t('rails.accountHolder')}><input value={holder} onChange={(e) => setHolder(e.target.value)} className={inputCn} /></Field>
        <Field label={t('rails.recipientType')}>
          <select value={type} onChange={(e) => setType(e.target.value as 'business' | 'personal')} className={inputCn}><option value="business">business</option><option value="personal">personal</option></select>
        </Field>
        <Field label={t('rails.reference')} hint="≤80"><input value={reference} onChange={(e) => setReference(e.target.value.slice(0, 80))} className={inputCn} /></Field>
      </div>
      <Field label={t('rails.wiseDetails')} hint="JSON — IBAN/ABA/sortCode per currency">
        <textarea value={detailsJson} onChange={(e) => setDetailsJson(e.target.value)} rows={4} className={`${inputCn} font-mono text-xs`} />
      </Field>
      <SubmitBtn onClick={submit} disabled={gen.isPending} label={t('rails.submitWise')} />
    </Section>
  );
}

export function RailGenerateForm(props: FormProps & { rail: 'swift' | 'ach' | 'sepa' | 'wise' }) {
  const { rail, ...rest } = props;
  if (rail === 'swift') return <SwiftForm {...rest} />;
  if (rail === 'ach') return <AchForm {...rest} />;
  if (rail === 'sepa') return <SepaForm {...rest} />;
  return <WiseForm {...rest} />;
}
